import { IState } from "@/apps/person-registration/models/IState";
import { IImageFeedbackState } from "../models/IImageFeedbackState";
import { IIdentificationState } from "@/apps/person-registration/models/IIdentificationState";

/**
 * MUTATIONS
 */
const mutations = {
  SET_USER_LOCATION: (state: IState, payload: string | null): void => {
    state.location = payload;
  },

  SET_USER_TYPE: (state: IState, payload: string | null): void => {
    state.userType = payload;
  },

  SET_USER_TAG: (state: IState, payload: string | null): void => {
    state.userTag = payload;
  },

  SET_VERSION_HASH: (state: IState, payload: number | null): void => {
    state.versionHash = payload;
  },

  SET_PRIVACY_POLICY_ACCEPT: (state: IState, payload: boolean): void => {
    state.privacyPolicyAccepted = payload;
  },

  SET_HAS_FRONT_ID: (state: IState, payload: boolean): void => {
    state.hasFrontId = payload;
  },

  SET_HAS_BACK_ID: (state: IState, payload: boolean): void => {
    state.hasBackId = payload;
  },

  SET_HAS_SELFIE: (state: IState, payload: boolean): void => {
    state.hasSelfie = payload;
  },

  SET_IN_MANUAL_REVIEW: (state: IState, payload: boolean): void => {
    state.inManualReview = payload;
  },

  SET_PHONE_VERIFICATION: (
    state: IState,
    payload: {
      phoneNumber: string | null;
      previouslyVerifiedPhone: string | null;
      isPreviouslyVerifiedPhoneFound: boolean;
      registrantOptedToUsePreviouslyVerifiedPhone: boolean;
      isPhoneVerified: boolean;
    }
  ): void => {
    state.phoneVerification.phoneNumber = payload.phoneNumber;
    state.phoneVerification.previouslyVerifiedPhone =
      payload.previouslyVerifiedPhone;
    state.phoneVerification.isPreviouslyVerifiedPhoneFound =
      payload.isPreviouslyVerifiedPhoneFound;
    state.phoneVerification.registrantOptedToUsePreviouslyVerifiedPhone =
      payload.registrantOptedToUsePreviouslyVerifiedPhone;
    state.phoneVerification.isPhoneVerified = payload.isPhoneVerified;
  },

  SET_PHONE_NUMBER: (state: IState, payload: string | null): void => {
    state.phoneVerification.phoneNumber = payload;
  },

  SET_IS_PHONE_VERIFIED: (state: IState, payload: boolean): void => {
    state.phoneVerification.isPhoneVerified = payload;
  },

  REGISTRANT_OPTED_TO_USE_PREVIOUSLY_VERIFIED_PHONE: (
    state: IState,
    payload: string
  ): void => {
    state.phoneVerification.phoneNumber = payload;
    state.phoneVerification.registrantOptedToUsePreviouslyVerifiedPhone = true;
    state.phoneVerification.isPhoneVerified = true;
  },

  SET_EMAIL_VERIFICATION: (
    state: IState,
    payload: {
      email: string | null;
      previouslyVerifiedEmail: string | null;
      isPreviouslyVerifiedEmailFound: boolean;
      registrantOptedToUsePreviouslyVerifiedEmail: boolean;
      isEmailVerified: boolean;
    }
  ): void => {
    state.emailVerification.email = payload.email;
    state.emailVerification.previouslyVerifiedEmail =
      payload.previouslyVerifiedEmail;
    state.emailVerification.isPreviouslyVerifiedEmailFound =
      payload.isPreviouslyVerifiedEmailFound;
    state.emailVerification.registrantOptedToUsePreviouslyVerifiedEmail =
      payload.registrantOptedToUsePreviouslyVerifiedEmail;
    state.emailVerification.isEmailVerified = payload.isEmailVerified;
  },

  SET_EMAIL: (state: IState, payload: string | null): void => {
    state.emailVerification.email = payload;
  },

  SET_IS_EMAIL_VERIFIED: (state: IState, payload: boolean): void => {
    state.emailVerification.isEmailVerified = payload;
  },

  REGISTRANT_OPTED_TO_USE_PREVIOUSLY_VERIFIED_EMAIL: (
    state: IState,
    payload: string
  ): void => {
    state.emailVerification.email = payload;
    state.emailVerification.registrantOptedToUsePreviouslyVerifiedEmail = true;
    state.emailVerification.isEmailVerified = true;
  },

  SET_PASSWORD_CREATED_AT: (state: IState, payload: number | null): void => {
    state.userData.passwordCreatedAt = payload;
  },

  SET_PERSON_CREATED: (state: IState, payload: boolean | null): void => {
    state.personCreated = payload;
  },

  SET_MESSAGES_BAG_FRONT: (
    state: IState,
    payload: IImageFeedbackState[]
  ): void => {
    state.messagesBag.front = payload;
  },

  SET_MESSAGES_BAG_BACK: (
    state: IState,
    payload: IImageFeedbackState[]
  ): void => {
    state.messagesBag.back = payload;
  },

  SET_MESSAGES_BAG_SELFIE: (
    state: IState,
    payload: IImageFeedbackState[]
  ): void => {
    state.messagesBag.selfie = payload;
  },

  SET_MESSAGES_BAG_BOTTOM_PAGE: (
    state: IState,
    payload: IImageFeedbackState[]
  ): void => {
    state.messagesBag.bottomPage = payload;
  },

  SET_MESSAGES_BAG_CX_CORRECTION: (
    state: IState,
    payload: IImageFeedbackState[]
  ): void => {
    state.messagesBag.cxCorrection = payload;
  },

  SET_ACTIONABLE_PAGE: (state: IState, payload: string | null): void => {
    state.actionablePage = payload;
  },

  SET_SETTINGS: (state: IState, payload: Array<string> | null): void => {
    state.settings = payload;
  },

  SET_EXTERNAL_URL_AFTER_COMPLETION: (
    state: IState,
    payload: string | null
  ): void => {
    state.externalUrlAfterCompletion = payload;
  },

  SET_CAPTURE_FLOW: (state: IState, payload: string | null): void => {
    state.captureFlow = payload;
  },

  SET_CAPTURE_FLOW_COMPLETED: (
    state: IState,
    payload: boolean | null
  ): void => {
    state.captureFlowCompleted = payload;
  },

  SET_COUNTRY: (state: IState, payload: string | null): void => {
    state.userData.country = payload;
  },
  SET_ORIGINATING_COUNTRY: (state: IState, payload: string | null): void => {
    state.userData.originatingCountry = payload;
  },
  SET_IDENTIFICATIONS: (
    state: IState,
    payload: Array<IIdentificationState>
  ): void => {
    state.userData.identifications = payload;
  },
  SET_SKIP_EMAIL_INPUT: (state: IState, payload: boolean): void => {
    state.skipEmailInput = payload;
  },
  SET_SKIP_PHONE_INPUT: (state: IState, payload: boolean): void => {
    state.skipPhoneInput = payload;
  },
  SET_HIDE_CANCEL_BUTTON: (state: IState, payload: boolean): void => {
    state.hideCancelButton = payload;
  },
  SET_INCODE_REDIRECTION_URL: (state: IState, payload: boolean): void => {
    state.incodeRedirectionUrl = payload;
  },
  SET_CX_QUEUE_POSITION: (state: IState, payload: number): void => {
    state.cxQueuePosition = payload;
  },
  SET_CX_TICKET_ASSIGNED: (state: IState, payload: boolean): void => {
    state.isCxTicketAssigned = payload;
  },
};

export default mutations;
